
import { useState, useCallback } from "react";
import { format, parseISO, isValid } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

export interface InlineDateFieldProps {
  label: string;
  /** ISO date string (YYYY-MM-DD) or null/undefined */
  value?: string | null;
  /** Called with YYYY-MM-DD string, or empty string to clear */
  onSave: (value: string) => void;
  icon?: React.ReactNode;
  emptyText?: string;
  className?: string;
}

function parseDate(value?: string | null): Date | undefined {
  if (!value) return undefined;
  try {
    const d = parseISO(value);
    return isValid(d) ? d : undefined;
  } catch {
    return undefined;
  }
}

export function InlineDateField({
  label,
  value,
  onSave,
  icon,
  emptyText,
  className,
}: InlineDateFieldProps) {
  const [open, setOpen] = useState(false);

  const selected = parseDate(value);

  const handleSelect = useCallback(
    (day: Date | undefined) => {
      setOpen(false);
      const next = day ? format(day, "yyyy-MM-dd") : "";
      if (next !== (value ?? "")) {
        onSave(next);
      }
    },
    [value, onSave]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (value) onSave("");
    },
    [value, onSave]
  );

  const displayValue = selected ? format(selected, "MMM d, yyyy") : null;

  return (
    <div className={cn("group/field flex items-start gap-2.5", className)}>
      {icon && <div className="mt-0.5 shrink-0 text-muted-foreground">{icon}</div>}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-0.5">{label}</p>

        <div className="group/val flex items-center gap-1.5 -mx-1 px-1 py-0.5 rounded-sm hover:bg-accent/40 transition-colors">
          <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 flex-1 min-w-0 text-left cursor-pointer"
            >
              <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
              {displayValue ? (
                <span className="text-sm flex-1 truncate">{displayValue}</span>
              ) : (
                <span className="text-sm text-muted-foreground/50 italic flex-1">
                  {emptyText ?? `Pick a date…`}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={handleSelect}
              autoFocus
              defaultMonth={selected}
            />
          </PopoverContent>
        </Popover>
          {displayValue && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 text-muted-foreground/30 hover:text-muted-foreground transition-opacity opacity-100 md:opacity-0 md:group-hover/val:opacity-100"
              tabIndex={-1}
              aria-label="Clear date"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
